import { ReactElement, SyntheticEvent } from 'react';
import { useIsLiked } from 'hooks/utils/useIsLiked';
import { ContentType } from '@novaheal/types';
import { useModalService } from 'hooks/useModalService';
import { ModalVariant } from 'types/Modal';
import { FavoriteIcon, FavoriteSolidIcon } from 'assets/icons/v2';
import { NovaIconButton } from 'components/ui/NovaIconButton';

type Props = {
  id: string;
  contentType: ContentType;
  name: string;
  shouldShowWarning?: boolean;
  afterLikeClickFn?: () => void;
  // Pass this when the caller already knows the like state without asking
  // the server (e.g. Favorites list items are liked by definition) — skips
  // the GET /user/likes lookup entirely. See useIsLiked.
  entryIsLiked?: boolean;
};

export const LikeButton = ({
  id,
  contentType,
  name,
  afterLikeClickFn,
  shouldShowWarning,
  entryIsLiked,
}: Props): ReactElement | null => {
  const { isLiked, handleLikeChange } = useIsLiked(id, contentType, entryIsLiked);
  const modalService = useModalService();

  const handleUpdateOfLike = async (): Promise<void> => {
    await handleLikeChange(name);
    if (afterLikeClickFn) afterLikeClickFn();
  };

  const handleClick = async (e: SyntheticEvent<unknown, Event>): Promise<void> => {
    e.stopPropagation();
    if (shouldShowWarning)
      modalService.show(ModalVariant.DISLIKE, { onAccept: async () => handleUpdateOfLike(), name });
    else {
      await handleUpdateOfLike();
    }
  };

  const shouldShowLikeButton = isLiked !== null;

  return shouldShowLikeButton ? (
    <NovaIconButton
      variant="primary"
      icon={isLiked ? FavoriteSolidIcon : FavoriteIcon}
      size="l"
      onClick={handleClick}
      color="icon.accent"
    />
  ) : null;
};
